import { useEffect, useRef } from "react";
import { Share, Plus } from "lucide-react";
import { toast } from 'sonner';
import { useIsMobile } from "@/hooks/useMobile";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const PWA_DISMISSED_KEY = "pwa-prompt-dismissed";
const PWA_TOAST_ID = "pwa-install-prompt";

function wasDismissedRecently(): boolean {
  const dismissedAt = localStorage.getItem(PWA_DISMISSED_KEY);
  if (!dismissedAt) return false;
  const dismissedTime = parseInt(dismissedAt, 10);
  return Date.now() - dismissedTime < TWENTY_FOUR_HOURS_MS;
}

export function PWAInstallPrompt() {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const toastShownRef = useRef(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    if (!isMobile || standalone || wasDismissedRecently()) {
      return;
    }

    const handleDismiss = () => {
      localStorage.setItem(PWA_DISMISSED_KEY, Date.now().toString());
      toast.dismiss(PWA_TOAST_ID);
      toastShownRef.current = false;
    };

    const showInstallToast = (
      promptEvent?: BeforeInstallPromptEvent | null,
    ) => {
      if (toastShownRef.current) return;
      toastShownRef.current = true;

      if (isIOS) {
        toast("Installer Concretly", {
          id: PWA_TOAST_ID,
          position: "bottom-center",
          description: (
            <span className="block text-muted-foreground">
              Partagez cette page puis choisissez « Sur l'écran d'accueil » :{" "}
              <Share className="inline h-3.5 w-3.5" /> puis{" "}
              <Plus className="inline h-3.5 w-3.5" /> « Sur l'écran d'accueil »
            </span>
          ),
          duration: Infinity,
          cancel: {
            label: "Fermer",
            onClick: handleDismiss,
          },
          onDismiss: handleDismiss,
        });
        return;
      }

      if (promptEvent) {
        deferredPromptRef.current = promptEvent;
        toast("Installer Concretly", {
          id: PWA_TOAST_ID,
          position: "bottom-center",
          description:
            "Accédez rapidement à Concretly depuis votre écran d'accueil",
          duration: Infinity,
          action: {
            label: "Installer",
            onClick: async () => {
              const p = deferredPromptRef.current;
              if (!p) return;
              await p.prompt();
              const { outcome } = await p.userChoice;
              if (outcome === "accepted") {
                toast.success("Application installée !");
              }
              deferredPromptRef.current = null;
            },
          },
          cancel: {
            label: "Fermer",
            onClick: handleDismiss,
          },
          onDismiss: handleDismiss,
        });
      }
    };

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      deferredPromptRef.current = promptEvent;
      showInstallToast(promptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    if (isIOS) {
      const timer = setTimeout(() => showInstallToast(null), 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener(
          "beforeinstallprompt",
          handleBeforeInstallPrompt,
        );
      };
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, [isMobile]);

  return null;
}
